import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  CHAT_EDIT_MINUTES,
  CHAT_REACTIONS,
  CHAT_TEXT_MAX,
  FORWARD_MAX,
  QUICK_REPLIES,
  STICKERS,
  STICKER_GROUPS,
  isChatEvent,
  socialLine,
  type ChatMessage,
  type ChatReactionEmoji,
  type Conversation,
  type StickerId,
} from '@london-life/contracts';
import type { FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { PersonPicture } from '../people/person-card';
import { GiftCard, MoneyCard } from './money';
import { InviteCard } from '../visits/invite-card';
import { ShopCard } from '../business/shop-card';
import { useDismiss } from '../lib/use-dismiss';
import { VoiceButton, VoicePlayer } from './voice';
import { notify } from '../lib/alerts';

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const canEdit = (message: ChatMessage) =>
  message.mine &&
  message.kind === 'text' &&
  !message.deletedAt &&
  Date.now() - new Date(message.createdAt).getTime() < CHAT_EDIT_MINUTES * 60_000;
const stickerLabel = (id: string) => STICKERS[id as StickerId] ?? 'Sticker';
const SWIPE_TO_REPLY = 56;
// "Today", "Yesterday" or "10 Oct": shown once above the first message of each day
const dayOf = (iso: string) => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export function StickerImage({ id, size = 120 }: { id: string; size?: number }) {
  return (
    <img
      className="chat-sticker"
      src={`/stickers/${id}.svg`}
      alt={stickerLabel(id)}
      width={size}
      height={size}
      draggable={false}
    />
  );
}

// choose up to five chats or groups to forward a message to
function ForwardDialog({
  currentId,
  onSend,
  onClose,
  busy,
}: {
  currentId: string | null;
  onSend: (ids: string[]) => Promise<boolean>;
  onClose: () => void;
  busy: boolean;
}) {
  const [targets, setTargets] = useState<Conversation[] | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  useEffect(() => {
    void api.chat
      .list()
      .then((reply) => setTargets([...reply.chats, ...reply.groups].filter((chat) => chat.id !== currentId)))
      .catch(() => setTargets([]));
  }, [currentId]);
  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : current.length < FORWARD_MAX
          ? [...current, id]
          : current,
    );
  return (
    <div className="chat-forward" role="dialog" aria-label="Forward to">
      <header>
        <b>Forward to</b>
        <small>
          {chosen.length}/{FORWARD_MAX}
        </small>
        <button type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      {targets && !targets.length && <p className="chat-empty">No other chats yet.</p>}
      <ul>
        {targets?.map((chat) => {
          const name = chat.kind === 'group' ? chat.group?.name : chat.other?.firstName;
          return (
            <li key={chat.id}>
              <label>
                <input type="checkbox" checked={chosen.includes(chat.id)} onChange={() => toggle(chat.id)} />
                {chat.kind === 'group' ? (
                  <span className="chat-group-icon">{chat.group?.icon ?? '👥'}</span>
                ) : (
                  chat.other && <PersonPicture person={chat.other} />
                )}
                <span>{name ?? 'Chat'}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="house-button save"
        disabled={busy || !chosen.length}
        onClick={() => void onSend(chosen).then((ok) => ok && onClose())}
      >
        Send
      </button>
    </div>
  );
}

// Messages and the box to write one: used for a place's chat, direct messages and groups. Reply, react,
// forward, edit, delete for everyone and report are on the message's menu (and a swipe right on a phone
// replies), so they work the same with a thumb and a mouse.
export function MessageList({
  messages,
  placeholder,
  sending,
  failure,
  hasOlder,
  onOlder,
  onSend,
  onEdit,
  onDelete,
  onReport,
  onReact,
  onForward,
  conversationId = null,
  myUserId = null,
  showSeen = false,
  compact = false,
  quickReplies = false,
  banner = null,
  voice = null,
  onVoice,
  emptyText,
  composer = true,
  money = null,
  visits = null,
  shops = null,
}: {
  messages: ChatMessage[];
  placeholder: string;
  sending: boolean;
  failure: FriendlyError | null;
  hasOlder: boolean;
  onOlder: () => void;
  onSend: (text: string, replyTo: string | null, sticker?: StickerId) => Promise<boolean>;
  onEdit: (id: string, text: string) => Promise<boolean>;
  onDelete: (id: string) => Promise<void>;
  onReport: (id: string, reason: string) => Promise<void>;
  onReact?: (id: string, emoji: ChatReactionEmoji | null) => Promise<void>;
  onForward?: (id: string, conversationIds: string[]) => Promise<boolean>;
  conversationId?: string | null;
  myUserId?: string | null;
  showSeen?: boolean;
  compact?: boolean;
  // one-tap phrases under the box (direct chats and groups)
  quickReplies?: boolean;
  // shown above the messages (who can see this chat, a message request's choices)
  banner?: ReactNode;
  // voice notes here: whether I may send one, and what to say when I may not
  voice?: { canSend: boolean; blockedText: string } | null;
  onVoice?: (recording: Blob, type: string, ms: number) => Promise<boolean>;
  emptyText: string;
  // the box to write in (a venue has its own, always on show)
  composer?: boolean;
  // money in a direct chat: the 💸 button, and paying or answering a request on its card
  money?: {
    otherName: string;
    busy: boolean;
    onOpen: () => void;
    onAnswer: (requestId: string, answer: 'pay' | 'decline' | 'cancel') => void;
  } | null;
  // visits from a direct chat: the 🏠 "Invite over" button, and "Go over" on an invite's card
  visits?: { busy: boolean; onInvite: () => void; onGoOver?: (hostLifeId: string) => void } | null;
  // players' shops: the 🛍️ "My shop" button (your shops with a shelf), and "Open shop" on a shop's card
  shops?: {
    mine: { id: string; name: string }[];
    busy: boolean;
    onShare: (businessId: string) => void;
    onOpen?: (businessId: string) => void;
  } | null;
}) {
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  // the open menu opens upwards when there is no room under it in the chat
  const [menuUp, setMenuUp] = useState(false);
  const menuBox = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = menuBox.current;
    const area = scroller.current;
    if (!menu || !box || !area) return setMenuUp(false);
    const room = area.getBoundingClientRect();
    const anchor = box.parentElement!.getBoundingClientRect();
    const tall = box.offsetHeight;
    // below if it fits; otherwise above if that fits better; then brought fully into view
    const below = room.bottom - anchor.bottom;
    const above = anchor.top - room.top;
    const up = tall > below && above > below;
    setMenuUp(up);
    // never past the chat's sides, whatever the width
    box.style.translate = '';
    const sides = box.getBoundingClientRect();
    const shift =
      sides.left < room.left + 4
        ? room.left + 4 - sides.left
        : sides.right > room.right - 4
          ? room.right - 4 - sides.right
          : 0;
    if (shift) box.style.translate = `${Math.round(shift)}px 0`;
    requestAnimationFrame(() => box.scrollIntoView({ block: 'nearest' }));
  }, [menu]);
  // a tap or click anywhere else, or Escape, closes the menu (the ⋯ that opened it toggles it)
  const moreButton = useRef<HTMLButtonElement>(null);
  useDismiss(Boolean(menu), () => setMenu(null), menuBox, moreButton);
  const [reporting, setReporting] = useState<string | null>(null);
  const [forwarding, setForwarding] = useState<string | null>(null);
  const [stickers, setStickers] = useState(false);
  const [stickerTab, setStickerTab] = useState(0);
  const [reason, setReason] = useState('');
  const [swipe, setSwipe] = useState<{ id: string; x: number; dx: number } | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const count = messages.length;
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [count]);
  const startReply = (message: ChatMessage) => {
    setReplyTo(message);
    setEditing(null);
    setMenu(null);
    input.current?.focus();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;
    const ok = editing ? await onEdit(editing.id, value) : await onSend(value, replyTo?.id ?? null);
    if (ok) {
      setText('');
      setReplyTo(null);
      setEditing(null);
    }
  };
  const sendSticker = async (id: StickerId) => {
    const ok = await onSend('', replyTo?.id ?? null, id);
    if (ok) {
      setStickers(false);
      setReplyTo(null);
    }
  };
  // a finger dragged right along a message replies to it
  const swipeHandlers = (message: ChatMessage) => ({
    onPointerDown: (event: ReactPointerEvent) => {
      if (event.pointerType === 'mouse' || isChatEvent(message.kind) || message.deletedAt) return;
      setSwipe({ id: message.id, x: event.clientX, dx: 0 });
    },
    onPointerMove: (event: ReactPointerEvent) => {
      if (swipe?.id !== message.id) return;
      setSwipe({ ...swipe, dx: Math.max(0, Math.min(90, event.clientX - swipe.x)) });
    },
    onPointerUp: () => {
      if (swipe?.id === message.id && swipe.dx >= SWIPE_TO_REPLY) startReply(message);
      setSwipe(null);
    },
    onPointerCancel: () => setSwipe(null),
  });
  // the last message someone else sent that they have seen: shown once, under it
  const lastSeen = showSeen ? [...messages].reverse().find((message) => message.mine && message.seen) : undefined;
  const myReaction = (message: ChatMessage) =>
    message.reactions.find((reaction) => myUserId && reaction.userIds.includes(myUserId))?.emoji ?? null;
  return (
    <div className={compact ? 'chat compact' : 'chat'}>
      <div className="chat-scroll" role="log" aria-live="polite" aria-relevant="additions" ref={scroller}>
        {hasOlder && (
          <button type="button" className="house-button quiet chat-older" onClick={onOlder} disabled={sending}>
            Earlier messages
          </button>
        )}
        {banner}
        {!messages.length && <p className="chat-empty">{emptyText}</p>}
        {messages.map((message, index) => {
          const day = dayOf(message.createdAt);
          const newDay = !compact && (index === 0 || dayOf(messages[index - 1]!.createdAt) !== day);
          return (
            <Fragment key={message.id}>
              {newDay && <p className="chat-day">{day}</p>}
              {isChatEvent(message.kind) ? (
                <p className={message.kind === 'social' ? 'chat-system social' : 'chat-system'}>
                  {message.kind === 'social'
                    ? socialLine(message.text ?? '', message.mine ? 'You' : message.sender.firstName)
                    : message.text}
                  {message.kind === 'social' && <small> · {timeOf(message.createdAt)}</small>}
                </p>
              ) : (
                <div
                  className={`chat-message${message.mine ? ' mine' : ''}${message.deletedAt ? ' deleted' : ''}${message.kind === 'sticker' && !message.deletedAt ? ' sticker' : ''}${message.kind === 'money' || message.kind === 'gift' || message.kind === 'invite' || message.kind === 'shop' ? ' money' : ''}`}
                  style={swipe?.id === message.id ? { transform: `translateX(${swipe.dx}px)` } : undefined}
                  {...swipeHandlers(message)}
                >
                  {!message.mine && !compact && <PersonPicture person={message.sender} />}
                  <div className="chat-bubble">
                    {!message.mine && (
                      <b className="chat-name">
                        {message.sender.firstName}
                        {message.sender.username && <small>@{message.sender.username}</small>}
                      </b>
                    )}
                    {message.forwarded && <small className="chat-forwarded">↪ Forwarded</small>}
                    {message.replyTo && (
                      <q className="chat-reply">
                        <b>{message.replyTo.firstName}</b> {message.replyTo.text ?? 'Message deleted'}
                      </q>
                    )}
                    {message.deletedAt ? (
                      <p>
                        <i>Message deleted</i>
                      </p>
                    ) : message.kind === 'voice' ? (
                      <VoicePlayer
                        playPath={message.voice?.playPath ?? null}
                        durationMs={message.voice?.durationMs ?? 0}
                      />
                    ) : message.kind === 'invite' && message.invite ? (
                      <InviteCard
                        message={message}
                        otherName={money?.otherName ?? 'them'}
                        onGoOver={visits?.onGoOver}
                      />
                    ) : message.kind === 'shop' && message.shop ? (
                      <ShopCard message={message} onOpen={shops?.onOpen} />
                    ) : message.kind === 'gift' && message.gift ? (
                      <GiftCard message={message} otherName={money?.otherName ?? 'them'} />
                    ) : message.kind === 'money' && message.money ? (
                      <MoneyCard
                        message={message}
                        otherName={money?.otherName ?? 'them'}
                        busy={money?.busy ?? false}
                        onAnswer={money?.onAnswer}
                      />
                    ) : message.kind === 'sticker' ? (
                      <StickerImage id={message.text ?? ''} size={compact ? 84 : 120} />
                    ) : (
                      <p>{message.text}</p>
                    )}
                    <small className="chat-meta">
                      {timeOf(message.createdAt)}
                      {message.editedAt && !message.deletedAt && ' · edited'}
                      {lastSeen?.id === message.id && ' · Seen'}
                    </small>
                    {message.reactions.length > 0 && (
                      <div className="chat-reactions" aria-label="Reactions">
                        {message.reactions.map((reaction) => {
                          const mine = !!myUserId && reaction.userIds.includes(myUserId);
                          return (
                            <button
                              key={reaction.emoji}
                              type="button"
                              className={mine ? 'mine' : undefined}
                              aria-pressed={mine}
                              aria-label={`${reaction.emoji} ${reaction.userIds.length}`}
                              disabled={!onReact || sending}
                              onClick={() =>
                                void onReact?.(message.id, mine ? null : (reaction.emoji as ChatReactionEmoji))
                              }
                            >
                              {reaction.emoji} {reaction.userIds.length}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {!message.deletedAt && (
                      <button
                        type="button"
                        className="chat-more"
                        aria-label={`More for this message from ${message.sender.firstName}`}
                        aria-expanded={menu === message.id}
                        ref={menu === message.id ? moreButton : undefined}
                        onClick={() => setMenu(menu === message.id ? null : message.id)}
                      >
                        ⋯
                      </button>
                    )}
                    {menu === message.id && (
                      <div
                        className={`chat-menu${menuUp ? ' up' : ''}${message.mine ? '' : ' left'}`}
                        role="group"
                        aria-label="Message actions"
                        ref={menuBox}
                      >
                        {onReact && (
                          <div className="chat-react-bar" role="group" aria-label="React">
                            {CHAT_REACTIONS.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                aria-label={`React ${emoji}`}
                                aria-pressed={myReaction(message) === emoji}
                                className={myReaction(message) === emoji ? 'mine' : undefined}
                                onClick={() => {
                                  setMenu(null);
                                  void onReact(message.id, myReaction(message) === emoji ? null : emoji);
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                        <button type="button" onClick={() => startReply(message)}>
                          ↩ Reply
                        </button>
                        {onForward &&
                          message.kind !== 'money' &&
                          message.kind !== 'gift' &&
                          message.kind !== 'invite' && (
                            <button type="button" onClick={() => (setForwarding(message.id), setMenu(null))}>
                              ↪ Forward
                            </button>
                          )}
                        {canEdit(message) && (
                          <button
                            type="button"
                            onClick={() => (
                              setEditing(message),
                              setReplyTo(null),
                              setText(message.text ?? ''),
                              setMenu(null)
                            )}
                          >
                            ✎ Edit
                          </button>
                        )}
                        {message.mine &&
                          message.kind !== 'money' &&
                          message.kind !== 'gift' &&
                          message.kind !== 'invite' && (
                            <button type="button" onClick={() => (setMenu(null), void onDelete(message.id))}>
                              🗑 Delete for everyone
                            </button>
                          )}
                        {!message.mine && (
                          <button type="button" onClick={() => (setReporting(message.id), setMenu(null))}>
                            ⚑ Report
                          </button>
                        )}
                      </div>
                    )}
                    {reporting === message.id && (
                      <form
                        className="chat-report"
                        onSubmit={(event) => {
                          event.preventDefault();
                          if (reason.trim().length < 3) return;
                          void onReport(message.id, reason.trim()).then(() => {
                            setReporting(null);
                            setReason('');
                          });
                        }}
                      >
                        <input
                          aria-label="Why are you reporting this?"
                          placeholder="What is wrong with it?"
                          value={reason}
                          maxLength={300}
                          onChange={(event) => setReason(event.target.value)}
                        />
                        <button
                          type="submit"
                          className="house-button save"
                          disabled={sending || reason.trim().length < 3}
                        >
                          Send report
                        </button>
                        <button type="button" className="house-button quiet" onClick={() => setReporting(null)}>
                          Cancel
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </Fragment>
          );
        })}
        <div ref={end} />
      </div>
      {forwarding && onForward && (
        <ForwardDialog
          currentId={conversationId}
          busy={sending}
          onClose={() => setForwarding(null)}
          onSend={(ids) => onForward(forwarding, ids)}
        />
      )}
      {failure && <ErrorNotice error={failure} />}
      {composer && (
        <form className="chat-compose" onSubmit={(event) => void submit(event)}>
          {(replyTo || editing) && (
            <div className="chat-composing">
              {editing
                ? 'Editing your message'
                : `Replying to ${replyTo!.sender.firstName}${replyTo!.kind === 'sticker' ? ` (${stickerLabel(replyTo!.text ?? '')})` : ''}`}
              <button
                type="button"
                aria-label="Cancel"
                onClick={() => {
                  setReplyTo(null);
                  setEditing(null);
                  setText('');
                }}
              >
                ×
              </button>
            </div>
          )}
          {stickers && (
            <div className="chat-sticker-tray" role="group" aria-label="Stickers">
              <div className="chat-sticker-tabs" role="tablist" aria-label="Sticker groups">
                {STICKER_GROUPS.map((group, index) => (
                  <button
                    key={group.label}
                    type="button"
                    role="tab"
                    aria-selected={stickerTab === index}
                    className={stickerTab === index ? 'active' : undefined}
                    onClick={() => setStickerTab(index)}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
              {STICKER_GROUPS[stickerTab]!.stickers.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-label={`Send sticker: ${STICKERS[id]}`}
                  disabled={sending}
                  onClick={() => void sendSticker(id)}
                >
                  <StickerImage id={id} size={64} />
                </button>
              ))}
            </div>
          )}
          {quickReplies && !stickers && !editing && !text && (
            <div className="chat-quick" role="group" aria-label="Quick replies">
              {QUICK_REPLIES.map((phrase) => (
                <button
                  key={phrase}
                  type="button"
                  disabled={sending}
                  onClick={() =>
                    void onSend(phrase, replyTo?.id ?? null).then((ok) => {
                      if (ok) setReplyTo(null);
                    })
                  }
                >
                  {phrase}
                </button>
              ))}
            </div>
          )}
          <div className="chat-compose-row">
            {!editing && (
              <button
                type="button"
                className={
                  stickers ? 'house-button active chat-sticker-toggle' : 'house-button quiet chat-sticker-toggle'
                }
                aria-label="Stickers"
                aria-pressed={stickers}
                onClick={() => setStickers(!stickers)}
              >
                🙂
              </button>
            )}
            {!editing && money && (
              <button
                type="button"
                className="house-button quiet chat-money-toggle"
                aria-label="Money"
                onClick={money.onOpen}
              >
                💸
              </button>
            )}
            {!editing && visits && (
              <button
                type="button"
                className="house-button quiet chat-money-toggle"
                aria-label="Invite over"
                disabled={visits.busy}
                onClick={visits.onInvite}
              >
                🏠
              </button>
            )}
            {!editing && shops && shops.mine.length > 0 && <ShopShareButton shops={shops} />}
            <input
              ref={input}
              aria-label={placeholder}
              placeholder={placeholder}
              value={text}
              maxLength={CHAT_TEXT_MAX}
              onChange={(event) => setText(event.target.value)}
              autoComplete="off"
              enterKeyHint="send"
            />
            {voice && onVoice && !editing && !text.trim() ? (
              <VoiceButton
                canSend={voice.canSend}
                blockedText={voice.blockedText}
                disabled={sending}
                onNote={notify}
                onSend={(recording, type, ms) =>
                  onVoice(recording, type, ms).then((ok) => {
                    if (ok) setReplyTo(null);
                    return ok;
                  })
                }
              />
            ) : (
              <button type="submit" className="house-button save" disabled={sending || !text.trim()}>
                {editing ? 'Save' : 'Send'}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

// 🛍️ "My shop": one shop shares at once; several ask which (a small menu that closes on a tap outside)
function ShopShareButton({ shops }: { shops: NonNullable<Parameters<typeof MessageList>[0]['shops']> }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  const share = (id: string) => {
    setOpen(false);
    shops.onShare(id);
  };
  return (
    <div className="chat-shop-share" ref={box}>
      <button
        type="button"
        className="house-button quiet chat-money-toggle"
        aria-label="My shop"
        aria-expanded={shops.mine.length > 1 ? open : undefined}
        disabled={shops.busy}
        onClick={() => (shops.mine.length === 1 ? share(shops.mine[0]!.id) : setOpen(!open))}
      >
        🛍️
      </button>
      {open && (
        <div className="chat-menu up" role="group" aria-label="Share a shop">
          {shops.mine.map((item) => (
            <button type="button" key={item.id} onClick={() => share(item.id)}>
              🛍️ {item.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
